import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree, RouterStateSnapshot } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AdminService } from '@modules/admin/services/admin.service';
import { MenuConfiguration } from '@modules/admin/interfaces/admin-url.interface';
import { MenuStructure } from '@shared/interfaces/interface';
import { MenuConfigurationService } from '@services/shared/menu-configuration/menu-configuration.service';
import { AuthService } from '@services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class SubMenuAccessGuard implements CanActivate {
  constructor(
    private readonly adminService: AdminService,
    private readonly router: Router,
    private readonly menuService: MenuConfigurationService,
    private readonly authService: AuthService
  ) { }
  canActivate(route: ActivatedRouteSnapshot,state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    // FULL url (handles deep links like /admin/configuration)
    const fullUrl = (state.url || '').toLowerCase();
    console.log('Attempting to access URL:', fullUrl);

    // Ensure admin status is computed when opening routes in a new tab/window
    this.authService.ensureAdminComputed();

    return combineLatest([
      this.adminService.menuItems$,
      this.authService.isAdmin$.pipe(filter(isAdmin => isAdmin !== null))
    ]).pipe(
      take(1),
      switchMap(([menu, isAdmin]) => {
        // If menu not ready, load it first
        if (!menu?.menuItems?.length) {
          return this.menuService.getAppConfiguration().pipe(
            map((data: MenuStructure) => {
              const menuConfig: MenuConfiguration = {
                menuItems: data.menuItems,
              };
              this.adminService.setMenuItems(menuConfig);
              return this.checkAccess(menuConfig, fullUrl, !!isAdmin);
            }),
          );
        }
        return of(this.checkAccess(menu, fullUrl, !!isAdmin));
      }),
    );
  }

  private checkAccess(menu: MenuConfiguration, fullUrl: string, isAdmin: boolean): boolean | UrlTree {

    // Hard block: non-admins cannot access /admin
    if (fullUrl.startsWith('/admin')) {
      return isAdmin ? true : this.router.createUrlTree(['/home']);
    }

    for (const menuItem of menu.menuItems || []) {
      for (const sub of menuItem.subMenuItems || []) {
        const subName = (sub.name || '').toLowerCase();

        // CAT Access Check
        if (fullUrl.includes('/cat') && subName.includes('convective alerting tool')) {

          // Admin override → allow even if inactive
          if (isAdmin) {
            return true;
          }

          // Non-admin → follow submenu isActive
          return sub.isActive === 1 ? true : this.router.createUrlTree(['/home']);
        }

        // FSR Access Check
        if (fullUrl.includes('/fsr') && subName.includes('fire & steam risk')) {

          // Admin override
          if (isAdmin) {
            return true;
          }

          // Non-admin → restrict based on toggle
          return sub.isActive === 1 ? true : this.router.createUrlTree(['/home']);
        }
      }
    }

    return true;
  }
}